import { isOrderComplete, OrdStatusEnum, type CareOrder } from '@talos/kyoko';
import type { IRowNode } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { isOrderRow, isQuoteRow, isQuoteRowWithOrder, type CareOrderBlotterEntity } from '../types';

export interface ManualFillCareOrderSelection {
  careOrder: CareOrder;
  /** Child RFQs/orders can block manual fill while they are still active. */
  childRows?: CareOrderBlotterEntity[];
}

export interface ManualFillCareOrdersValidationResult {
  manuallyFillable: boolean;
  reason?: string;
}

const MANUAL_FILLABLE_CARE_ORDER_STATUSES = new Set<OrdStatusEnum>([
  OrdStatusEnum.New,
  OrdStatusEnum.PartiallyFilled,
  OrdStatusEnum.Filled,
]);

export function isManualFillableCareOrderStatus(status: OrdStatusEnum) {
  return MANUAL_FILLABLE_CARE_ORDER_STATUSES.has(status);
}

/**
 * Shared eligibility check for every manual-fill entry point.
 *
 * Keep this independent from React/AG Grid rendering concerns so toolbar actions,
 * context menu items, and unit tests all enforce the same rules and disabled reason.
 */
export function validateManualFillCareOrders(
  selections: ManualFillCareOrderSelection[]
): ManualFillCareOrdersValidationResult {
  if (selections.length === 0) {
    return {
      manuallyFillable: false,
      reason: 'Select at least 1 care order to manually fill',
    };
  }

  const unfillableCareOrder = selections.find(({ careOrder }) => !careOrder.isManuallyFillable.result)?.careOrder;
  if (unfillableCareOrder != null) {
    return {
      manuallyFillable: false,
      reason: unfillableCareOrder.isManuallyFillable.failureReason,
    };
  }

  if (selections.some(selection => !areAllChildOrdersTerminal(selection.childRows ?? []))) {
    return {
      manuallyFillable: false,
      reason: 'All child orders must be terminal before care orders can be manually filled',
    };
  }

  return {
    manuallyFillable: true,
  };
}

export function getCareOrderBlotterChildRows(node: IRowNode<CareOrderBlotterEntity>): CareOrderBlotterEntity[] {
  // AG Grid exposes different child collections depending on grouping/filter state.
  const childNodes = node.childrenAfterGroup ?? node.childrenAfterFilter;
  return compact(childNodes?.map(childNode => childNode.data));
}

function areAllChildOrdersTerminal(childRows: CareOrderBlotterEntity[]): boolean {
  return childRows.every(childRow => {
    if (isOrderRow(childRow)) {
      return childRow.isComplete;
    }

    if (isQuoteRow(childRow)) {
      if (isQuoteRowWithOrder(childRow)) {
        return isOrderComplete(childRow.OrdStatus);
      }

      return childRow.isTerminal;
    }

    return true;
  });
}
